import type { LucideIcon, LucideProps } from "lucide-react";

/**
 * An icon that knows whether it should turn round in Arabic.
 *
 * Most do not. A clock, a trash can, a warehouse, a printer — these are
 * pictures of objects, and an object does not mirror because the text beside it
 * does. Flipping them produces a backwards clock face and a left-handed
 * printer, which reads as a rendering bug to the one audience it was meant to
 * serve.
 *
 * The ones that *must* flip are the ones that mean **direction** rather than
 * depict a thing: a chevron that means "forward" points right in English and
 * left in Arabic, because forward is where the text is going. Same for back
 * arrows, undo and redo, and sign-in and sign-out — those are doors, and the
 * direction through them is the reading direction.
 *
 * So `mirror` is opt-in per use rather than a list this component keeps.
 * `ChevronRight` mirrors when it is a "next" affordance and does not when it is
 * pointing at something on the right of a diagram, and only the call site knows
 * which. {@link MIRRORED} names the usual suspects so a reviewer can check a
 * screen against it.
 */

/**
 * The icons that almost always want `mirror`, from `10/06` §3.
 *
 * A checklist for review, not an enforcement: the component cannot tell what a
 * chevron means in a particular place.
 */
export const MIRRORED = [
	"ChevronRight",
	"ChevronLeft",
	"ArrowLeft",
	"ArrowRight",
	"ArrowRightToLine",
	"ArrowLeftToLine",
	"Undo",
	"Undo2",
	"Redo",
	"Redo2",
	"LogIn",
	"LogOut",
	"CornerDownLeft",
	"Reply",
] as const;

export interface IconProps extends Omit<LucideProps, "ref"> {
	/** The lucide icon to draw. */
	as: LucideIcon;
	/**
	 * Turn the drawing round when the document reads right to left.
	 *
	 * For icons that mean a direction. Never for icons that depict a thing.
	 */
	mirror?: boolean;
	/**
	 * An accessible name.
	 *
	 * Omit it — the default — and the icon is hidden from assistive technology,
	 * which is right whenever there is a text label beside it. An icon-only
	 * button needs one, and without it the button has no name at all.
	 */
	title?: string;
}

/**
 * The default size.
 *
 * 16px, matching the design system's body text. Lucide's own default is 24,
 * which is a heading-sized icon beside a 13px table cell.
 */
const DEFAULT_SIZE = 16;

export function Icon({
	as: Glyph,
	mirror = false,
	title,
	size = DEFAULT_SIZE,
	className,
	...rest
}: IconProps) {
	// `rtl:-scale-x-100` rather than a CSS file: Tailwind's `rtl:` variant
	// compiles to `[dir="rtl"] &`, which is the selector 10/06 §3 asks for, and
	// it stays on the element so a reader can see that this icon flips.
	const classes = [mirror ? "rtl:-scale-x-100" : "", className ?? ""].filter(Boolean).join(" ");

	return (
		<Glyph
			size={size}
			className={classes === "" ? undefined : classes}
			role={title === undefined ? undefined : "img"}
			aria-hidden={title === undefined ? true : undefined}
			aria-label={title}
			{...rest}
		/>
	);
}
